import { db, functions } from "@/main";
import { GenericDeviceProjectAssignment, GenericMeterReadingInfo } from "../models/GenericDeviceAssignment";
import { getClientRef } from "./ClientRepository";
import { firestore } from "firebase";
import { ProjectDetails, ProjectOperationSource } from "../models/ProjectDetails";
import { updateDeviceOverviews } from "./GenericDeviceRepository";

export function getDeviceAssignmentRef() {
  return `${getClientRef()}/deviceAssignment`;
}

export async function getDeviceAssignments(
  inventoryNumber: string
): Promise<GenericDeviceProjectAssignment[]> {
  const deviceAssignments = (
    await db
      .collection(getDeviceAssignmentRef())
      .where("assignedDeviceInventoryNumber", "==", inventoryNumber)
      .get()
  ).docs.map((doc) => doc.data() as GenericDeviceProjectAssignment);

  return deviceAssignments;
}

export async function getDeviceAssignmentsByDeviceId(
  deviceId: string
): Promise<GenericDeviceProjectAssignment[]> {
  const deviceAssignments = (
    await db
      .collection(getDeviceAssignmentRef())
      .where("assignedDeviceId", "==", deviceId)
      .get()
  ).docs.map((doc) => doc.data() as GenericDeviceProjectAssignment);

  return deviceAssignments;
}

export function observeGenericDeviceAssignmentsOfProject(
  projectId: string,
  onNext: (genericDeviceAssignments: Array<GenericDeviceProjectAssignment>) => void
) {
  return db
    .collection(getDeviceAssignmentRef())
    .where("assignedProjectId", "==", projectId)
    .onSnapshot((snapshot) => {
      onNext(snapshot.docs.map((doc) => doc.data() as GenericDeviceProjectAssignment));
    });
}

export async function getGenericDeviceAssignmentsOfProject(projectId: string) {
  return (
    await db
      .collection(getDeviceAssignmentRef())
      .where("assignedProjectId", "==", projectId)
      .get()
  ).docs.map((doc) => doc.data() as GenericDeviceProjectAssignment);
}

export async function updateExternalMeterDatesOfAssignedDevices(
  assignmentIds: string[] | null | undefined,
  projectId: string,
  newStartDate: firestore.Timestamp,
  newEndDate: firestore.Timestamp | null
) {
  if (!assignmentIds?.length) {
    return;
  }

  const assignments = (await getGenericDeviceAssignmentsOfProject(projectId))
    .filter((assignment) => assignmentIds.includes(assignment.id))
    .filter((assignment) => !!assignment.externalElectricityMeterInfo);

  const batch = db.batch();
  const deviceIds = new Set<string>();

  assignments.forEach((assignment) => {
      const assignmentRef = `${getDeviceAssignmentRef()}/${assignment.id}`;
      batch.set(
        db.doc(assignmentRef),
        {
          ...assignment,
          externalElectricityMeterInfo: assignment.externalElectricityMeterInfo
            ? {
                ...assignment.externalElectricityMeterInfo,
                assignmentStartDate: newStartDate,
                assignmentEndDate: newEndDate,
              }
            : null,
        },
        { merge: true }
      );
      deviceIds.add(assignment.assignedDeviceId);
    });

  await batch.commit();
  await updateDeviceOverviews(Array.from(deviceIds));
}

function serializeMeterReadingInfo(mri: GenericMeterReadingInfo | null | undefined) {
  if (!mri) return mri;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = { ...mri };
  const recordInfoFields = [
    'meterReadingStartCorrectedBy', 'meterReadingEndCorrectedBy',
    'startDateCorrectedBy', 'endDateCorrectedBy',
    'meterReadingStartEditedBy', 'startDateMeterReadingEditedBy'
  ] as const;
  for (const field of recordInfoFields) {
    if (result[field]?.recordedAt) {
      result[field] = {
        ...result[field],
        recordedAt: (result[field].recordedAt as firestore.Timestamp).toDate().toISOString(),
      };
    }
  }
  return result;
}

/**
 * Persists a generic device assignment via the saveGenericDeviceAssignment
 * Cloud Function. The Cloud Function handles all server-side effects:
 * assignment doc, device projection, device overviews, project overview,
 * project steps, and dependent external-meter assignment updates.
 */
export async function saveGenericDeviceAssignment(
  genericDeviceAssignment: GenericDeviceProjectAssignment,
  deviceUpdatePatch: Record<string, unknown> = {},
  dependentAssignmentIds: string[] = [],
  changeMadeBy?: string,
  changeMadeByUserId?: string,
  source: string = ProjectOperationSource.PORTAL
) {
  const saveAssignment = functions.httpsCallable("saveGenericDeviceAssignment");

  await saveAssignment({
    assignment: {
      ...genericDeviceAssignment,
      assignmentStartDate: genericDeviceAssignment.assignmentStartDate.toDate().toISOString(),
      assignmentEndDate: genericDeviceAssignment.assignmentEndDate?.toDate().toISOString() ?? null,
      createdBy: genericDeviceAssignment.createdBy ? {
        ...genericDeviceAssignment.createdBy,
        recordedAt: (genericDeviceAssignment.createdBy.recordedAt as firestore.Timestamp).toDate().toISOString(),
      } : undefined,
      meterReadingInfo: serializeMeterReadingInfo(genericDeviceAssignment.meterReadingInfo),
      externalElectricityMeterInfo: genericDeviceAssignment.externalElectricityMeterInfo ? {
        ...genericDeviceAssignment.externalElectricityMeterInfo,
        assignmentStartDate: genericDeviceAssignment.externalElectricityMeterInfo.assignmentStartDate.toDate().toISOString(),
        assignmentEndDate: genericDeviceAssignment.externalElectricityMeterInfo.assignmentEndDate?.toDate().toISOString() ?? null,
      } : null,
    },
    deviceUpdatePatch,
    dependentAssignmentIds,
    changeMadeBy,
    changeMadeByUserId,
    source,
  });
}

export async function deleteGenericDeviceAssignment(
  genericDeviceAssignment: GenericDeviceProjectAssignment
) {
  const deleteAssignment = functions.httpsCallable("deleteGenericDeviceAssignment");

  await deleteAssignment({
    assignment: {
      ...genericDeviceAssignment,
      assignmentStartDate: genericDeviceAssignment.assignmentStartDate.toDate().toISOString(),
      assignmentEndDate: genericDeviceAssignment.assignmentEndDate?.toDate().toISOString() ?? null,
      createdBy: genericDeviceAssignment.createdBy ? {
        ...genericDeviceAssignment.createdBy,
        recordedAt: (genericDeviceAssignment.createdBy.recordedAt as firestore.Timestamp).toDate().toISOString(),
      } : undefined,
      externalElectricityMeterInfo: genericDeviceAssignment.externalElectricityMeterInfo ? {
        ...genericDeviceAssignment.externalElectricityMeterInfo,
        assignmentStartDate: genericDeviceAssignment.externalElectricityMeterInfo.assignmentStartDate.toDate().toISOString(),
        assignmentEndDate: genericDeviceAssignment.externalElectricityMeterInfo.assignmentEndDate?.toDate().toISOString() ?? null,
      } : null,
    },
  });
}

export async function deleteGenericDeviceAssignments(
  _targetProject: ProjectDetails,
  assignments: Array<GenericDeviceProjectAssignment>
) {
  if (assignments.length === 0) {
    return;
  }

  await Promise.all(
    assignments.map((assignment) => deleteGenericDeviceAssignment(assignment))
  );
}

/**
 * Retrieves all assignments for a given device id that either started or ended after the specified timestamp.
 *
 * @param {string} assignedDeviceId - The id to filter assignments by.
 * @param {firestore.Timestamp} timestamp - The timestamp to filter assignments. Only assignments started or ended after this timestamp will be returned.
 * @returns {Promise<GenericDeviceProjectAssignment[]>} A promise that resolves to an array of GenericDeviceProjectAssignment objects.
 */
export async function getAllGenericDeviceAssignmentsStartedOrEndedAfter(
  assignedDeviceId: string,
  timestamp: firestore.Timestamp
) {
  // Query for assignments that started after the given date
  const startAfterAssignments = (
    await db
      .collection(getDeviceAssignmentRef())
      .where("assignedDeviceId", "==", assignedDeviceId)
      .where("assignmentStartDate", ">", timestamp.toDate())
      .orderBy("assignmentStartDate", "asc")
      .orderBy("assignmentEndDate", "desc")
      .get()
  ).docs.map((doc) => doc.data() as GenericDeviceProjectAssignment);

  // Query for assignments that ended after the given date
  const endAfterAssignments = (
    await db
      .collection(getDeviceAssignmentRef())
      .where("assignedDeviceId", "==", assignedDeviceId)
      .where("assignmentEndDate", ">", timestamp.toDate())
      .orderBy("assignmentEndDate", "desc")
      .get()
  ).docs.map((doc) => doc.data() as GenericDeviceProjectAssignment);

  // Combine the results, removing duplicates
  const allAssignmentsMap = new Map<string, GenericDeviceProjectAssignment>();
  [...startAfterAssignments, ...endAfterAssignments].forEach((assignment) => {
    allAssignmentsMap.set(assignment.id, assignment);
  });

  const allAssignments = Array.from(allAssignmentsMap.values());

  // Sort the combined assignments
  allAssignments.sort((a: GenericDeviceProjectAssignment, b: GenericDeviceProjectAssignment) => {
    const startDateComparison = a.assignmentStartDate.toMillis() - b.assignmentStartDate.toMillis();
    if (startDateComparison !== 0) {
      return startDateComparison;
    }
    if (a.assignmentEndDate === null) return 1;
    if (b.assignmentEndDate === null) return -1;
    return b.assignmentEndDate.toMillis() - a.assignmentEndDate.toMillis();
  });

  return allAssignments;
}
